import { Box } from '@mui/material';
import { number, object, string } from 'prop-types';
import React, { useRef, useState, useCallback } from 'react';
import getImageDimensions from 'utils/getImageDimensions';
import { I18n } from 'utils/i18n';
import ContentTracking from 'services/api/ContentTracking';
import PlayButton from './PlayButton';

const VideoThumbnail = ({ data, maxWidth = null, maxHeight = null, messageId = null }) => {
  let { width, height } = getImageDimensions(data.meta);
  if (maxWidth && width >= height && maxWidth < width) {
    const scale = maxWidth / width;
    width *= scale;
    height *= scale;
  }
  if (maxHeight && width <= height && maxHeight < height) {
    const scale = maxHeight / height;
    width *= scale;
    height *= scale;
  }
  const video = useRef();
  const [paused, setPaused] = useState(true);
  const [error, setError] = useState(false);
  const [firstPlay, setFirstPlay] = useState(true);
  const lastTrackedDecile = useRef(0);
  const lastTrackedTime = useRef(0);

  const sendVideoTracking = useCallback(() => {
    if (!messageId || !video.current) return;
    const v = video.current;
    lastTrackedTime.current = v.currentTime || 0;
    ContentTracking.trackVideoWatch({
      messageId,
      attachmentId: data._id,
      durationWatchedMs: Math.round((v.currentTime || 0) * 1000),
      totalDurationMs: Math.round((v.duration || 0) * 1000),
    });
  }, [messageId, data._id]);

  const handleTimeUpdate = useCallback(() => {
    if (!messageId || !video.current) return;
    const v = video.current;
    if (!v.duration) return;
    const pct = v.currentTime / v.duration;
    const decile = Math.floor(pct * 10);
    const elapsed = v.currentTime - lastTrackedTime.current;
    if (decile > lastTrackedDecile.current && elapsed >= 5) {
      lastTrackedDecile.current = decile;
      sendVideoTracking();
    }
  }, [messageId, sendVideoTracking]);

  const play = () => {
    setPaused(false);
    return video.current.play();
  };

  const handleLoaded = () => {
    setError(false);
  };

  const handleEnded = () => {
    lastTrackedDecile.current = 10;
    lastTrackedTime.current = 0;
    sendVideoTracking();
    setFirstPlay(true);
    setPaused(true);
  };

  const pause = () => {
    video.pause();
    setPaused(true);
  };

  const togglePlay = event => {
    event.preventDefault();
    setFirstPlay(false);
    if (paused) {
      play();
    } else {
      pause();
    }
  };

  const handleError = () => {
    setError(true);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width,
        height,
        overflow: 'hidden',
        backgroundColor: '#111',
      }}
    >
      {firstPlay && (
        <Box
          component="button"
          type="button"
          onClick={togglePlay}
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            zIndex: 10,
            justifyContent: 'center',
            background: 'transparent',
            border: 'none',
            padding: 0,
          }}
        >
          <PlayButton paused={paused} loaded={!error} onClick={togglePlay} />
        </Box>
      )}
      <Box
        component="video"
        onError={handleError}
        onEnded={handleEnded}
        onLoadedData={handleLoaded}
        onTimeUpdate={handleTimeUpdate}
        controls={!paused}
        poster={data.meta?.thumb}
        preload="none"
        ref={video}
        sx={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          display: 'block',
          backgroundColor: '#111',
        }}
      >
        <source src={data.httpLink} type={data.mimeType} />
        {I18n.t('VideoThumbnail.browserNotSupported')}
      </Box>
    </Box>
  );
};

VideoThumbnail.propTypes = {
  data: object.isRequired,
  maxWidth: number,
  maxHeight: number,
  messageId: string,
};

export default VideoThumbnail;
